import { UtilFormTypes } from "models/formTypes";
import { DateHelper } from "utils/helpers";
import { WintSuperkollAPIModelTimeReporting as ApiModel } from "wint-api-client/dist/types";

function useTimeReportingInvoiceService() {
  const mapReportDataToInvoiceDocumentation = (
    reportData?: ApiModel.TimeReportingReportRow[],
    timeReportingProjectId?: string,
    period?: Date,
    dateInterval?: UtilFormTypes.DateRange
  ) => {
    if (!dateInterval && !period) {
      return undefined;
    }

    if (!timeReportingProjectId || !reportData) {
      return undefined;
    }

    let periodStart;
    let periodEnd;
    if (!period) {
      const projectDetails = reportData.filter(
        (r) =>
          r.timeReportingReportRowType === ApiModel.TimeReportingReportRowType.ProjectDetails &&
          r.columns?.length &&
          // eslint-disable-next-line eqeqeq --- supressed to avoid regression testing if changed
          r.columns[rowDataColIndex].timeReportingProjectId == timeReportingProjectId &&
          r.columns[rowDataColIndex].billingModel !== undefined
      );
      if (!dateInterval?.start) {
        const dates = projectDetails.map((pd) => pd.columns![0].fromDate);
        periodStart = DateHelper.getMinDate(dates);
      } else {
        periodStart = dateInterval.start;
      }

      if (!dateInterval?.end && dateInterval?.start) {
        const dates: Date[] = projectDetails.map((pd) => pd.columns![0].toDate as Date).filter((d) => d !== undefined);
        periodEnd = DateHelper.getMaxDate(dates);
      } else {
        periodEnd = dateInterval?.end;
      }
    } else {
      periodStart = DateHelper.getFirstDayOfMonth(period);
      periodEnd = DateHelper.getLastDayOfMonth(period);
    }

    const personCategoryTimes = getInvoiceDocumentationPersonCategoryTimes(reportData, timeReportingProjectId);
    if (personCategoryTimes.length === 0) {
      return undefined;
    }

    return {
      timeReportingProjectId: timeReportingProjectId,
      fromDate: periodStart,
      toDate: periodEnd,
      personCategoryTimes: personCategoryTimes,
    } as ApiModel.TimeReportInvoiceDocumentation;
  };

  const hasUnattestedRecords = (timeReportingProjectId: string, reportData?: ApiModel.TimeReportingReportRow[]) => {
    if (!reportData || reportData.length === 0) {
      return false;
    }
    return getProjectCategorySummariesWithInvoiceDocumentation(reportData, timeReportingProjectId).some(
      (x) => x.columns && x.columns[0].timeReportStatus !== ApiModel.TimeReportStatus.Attested
    );
  };

  const getProjectCategorySummariesWithInvoiceDocumentation = (
    reportData: ApiModel.TimeReportingReportRow[],
    timeReportingProjectId: string
  ) => {
    const projectSummariesWithInvoiceDocumentation: ApiModel.TimeReportingReportRow[] = [];
    reportData.forEach((personSummaryRow) => {
      const projectSummaryRow = personSummaryRow.children?.find(
        (r) =>
          r.timeReportingReportRowType === ApiModel.TimeReportingReportRowType.ProjectSummary &&
          r.columns?.length &&
          // eslint-disable-next-line eqeqeq --- supressed to avoid regression testing if changed
          r.columns[rowDataColIndex].timeReportingProjectId == timeReportingProjectId &&
          r.columns[rowDataColIndex].billingModel !== undefined
      );

      const projectCategorySummaryRows = projectSummaryRow?.children?.filter(
        (r) =>
          r.timeReportingReportRowType === ApiModel.TimeReportingReportRowType.ProjectCategorySummary &&
          r.columns?.length &&
          // eslint-disable-next-line eqeqeq --- supressed to avoid regression testing if changed
          r.columns[rowDataColIndex].timeReportingProjectId == timeReportingProjectId &&
          r.columns[rowDataColIndex].billingModel !== undefined
      );

      projectCategorySummaryRows?.forEach((r) => {
        if (r!.columns![rowDataColIndex].hoursToInvoice) {
          projectSummariesWithInvoiceDocumentation.push(r);
        }
      });
    });
    return projectSummariesWithInvoiceDocumentation;
  };

  const getInvoiceDocumentationPersonCategoryTimes = (
    reportData: ApiModel.TimeReportingReportRow[],
    timeReportingProjectId: string
  ) => {
    const personCategoryProjectTimes: ApiModel.InvoiceDocumentationPersonCategoryTimes[] = [];

    getProjectCategorySummariesWithInvoiceDocumentation(reportData, timeReportingProjectId).forEach(
      (projectCategorySummaryRow) => {
        const rowData = projectCategorySummaryRow!.columns![rowDataColIndex];

        personCategoryProjectTimes.push({
          personId: projectCategorySummaryRow!.personId,
          categoryId: rowData.categoryId,
          hoursToInvoice: rowData.hoursToInvoice!,
          projectTimes:
            rowData.billingModel === ApiModel.TimeReportingPersonProjectBillingModel.Hourly
              ? getInvoiceDocumentationProjectTimes(projectCategorySummaryRow)
              : [],
        });
      }
    );

    return personCategoryProjectTimes;
  };

  const getInvoiceDocumentationProjectTimes = (projectCategorySummaryRow: ApiModel.TimeReportingReportRow) => {
    const projectTimes: ApiModel.InvoiceDocumentationProjectTime[] = [];

    projectCategorySummaryRow.children
      ?.filter(
        (row: ApiModel.TimeReportingReportRow) =>
          row.columns?.length &&
          row.columns[rowDataColIndex].hoursToInvoice &&
          row.columns[rowDataColIndex].projectTimeId
      )
      .map((projectDetailRow: ApiModel.TimeReportingReportRow) =>
        projectTimes.push({
          hoursToInvoice: projectDetailRow.columns![rowDataColIndex].hoursToInvoice!,
          projectTimeId: projectDetailRow.columns![rowDataColIndex].projectTimeId!,
        })
      );

    return projectTimes;
  };

  const rowDataColIndex = 0;

  return {
    mapReportDataToInvoiceDocumentation,
    hasUnattestedRecords,
    getProjectCategorySummariesWithInvoiceDocumentation,
  };
}

export default useTimeReportingInvoiceService;
